import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { fingerfoodData, saucesData, dressingsData, drinksData, sectionImages } from '../data/menuData';
import { Leaf, Star, Sparkles, Flame } from 'lucide-react';

const FINGERFOOD_BG = sectionImages.fingerfood;
const DRINKS_BG = sectionImages.drinks;

const FingerfoodDrinksSection = () => {
  const { language, t } = useLanguage();

  const renderBadges = (item) => {
    const badges = [];
    if (item.isPopular) badges.push({ icon: <Star className="w-4 h-4 text-amber-400 fill-amber-400" />, key: 'popular' });
    if (item.isVegetarian) badges.push({ icon: <Leaf className="w-4 h-4 text-emerald-500" />, key: 'vegetarian' });
    if (item.isSpicy) badges.push({ icon: <Flame className="w-4 h-4 text-orange-500" />, key: 'spicy' });
    
    if (badges.length === 0) return null;
    return (
      <div className="flex items-center gap-1 ml-1">
        {badges.map((b) => <span key={b.key}>{b.icon}</span>)}
      </div>
    );
  };

  return (
    <>
      {/* FINGERFOOD SECTION */}
      <section 
        id="fingerfood" 
        className="relative overflow-hidden"
        data-testid="fingerfood-section"
      >
        {/* Premium Section Header */}
        <div className="relative h-56 md:h-72 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${FINGERFOOD_BG})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-hanse-dark/50 via-hanse-dark/60 to-hanse-dark/95" />
          
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="flex items-center gap-2 mb-4 bg-gradient-to-r from-orange-500/20 to-orange-600/10 backdrop-blur-sm px-4 py-2 rounded-full border border-orange-400/30"
            >
              <Sparkles className="w-5 h-5 text-orange-400" />
              <span className="text-orange-300 font-semibold text-sm uppercase tracking-wider">
                {language === 'de' ? 'Perfekt zum Teilen' : 'Perfect for Sharing'}
              </span>
              <Sparkles className="w-5 h-5 text-orange-400" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3"
            >
              {t('menu.fingerfood.title')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-white/80 text-lg md:text-xl max-w-2xl"
            >
              {t('menu.fingerfood.subtitle')}
            </motion.p>
          </div>
        </div>

        {/* Premium Menu Content */}
        <div className="bg-gradient-to-b from-hanse-dark to-hanse-navy py-10 md:py-14">
          <div className="container mx-auto px-4 max-w-6xl">
            
            {/* Fingerfood Items - Premium Grid */}
            <div className="grid md:grid-cols-2 gap-3 mb-12">
              {fingerfoodData.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(index * 0.03, 0.2) }}
                  className="group relative bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-orange-400/30 hover:bg-white/10 transition-all duration-300"
                  data-testid={`fingerfood-item-${item.id}`}
                >
                  {item.isPopular && (
                    <div className="absolute -top-2 right-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      <Star className="w-3 h-3 inline mr-1 fill-white" />
                      {language === 'de' ? 'Beliebt' : 'Popular'}
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h4 className="font-bold text-white group-hover:text-orange-300 transition-colors">
                          {language === 'de' ? item.name : (item.nameEn || item.name)}
                        </h4>
                        {renderBadges(item)}
                      </div>
                      {item.description && (
                        <p className="text-white/50 text-sm mt-1">
                          {language === 'de' ? item.description : (item.descriptionEn || item.description)}
                        </p>
                      )}
                    </div>
                    <span className="font-bold text-amber-400 text-lg">€{item.price.toFixed(2)}</span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Dressings & Sauces - Premium Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h3 className="font-heading text-2xl font-bold text-white mb-6 text-center flex items-center justify-center gap-3">
                <span className="h-px w-12 bg-gradient-to-r from-transparent to-amber-400/50"></span>
                {t('menu.dressings.title')}
                <span className="h-px w-12 bg-gradient-to-l from-transparent to-amber-400/50"></span>
              </h3>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Dressings */}
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                  <h4 className="font-bold text-white mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <Leaf className="w-4 h-4 text-emerald-400" />
                    </span>
                    Dressings
                  </h4>
                  <div className="space-y-3">
                    {dressingsData.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0"
                        data-testid={`dressing-${item.id}`}
                      >
                        <span className="text-white/80 text-sm">
                          {language === 'de' ? item.name : (item.nameEn || item.name)}
                        </span>
                        <span className="text-amber-400 font-bold text-sm">€{item.price.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sauces */}
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                  <h4 className="font-bold text-white mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center">
                      <Flame className="w-4 h-4 text-orange-400" />
                    </span>
                    Saucen
                  </h4>
                  <div className="space-y-3">
                    {saucesData.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start justify-between py-2 border-b border-white/5 last:border-b-0"
                        data-testid={`sauce-${item.id}`}
                      >
                        <div className="flex-1">
                          <span className="text-white/80 text-sm font-medium flex items-center gap-1">
                            {language === 'de' ? item.name : (item.nameEn || item.name)}
                            {item.isSpicy && <Flame className="w-3 h-3 text-orange-500" />}
                          </span>
                          {item.description && (
                            <p className="text-white/40 text-xs mt-0.5">
                              {language === 'de' ? item.description : (item.descriptionEn || item.description)}
                            </p>
                          )}
                        </div>
                        <span className="text-amber-400 font-bold text-sm ml-2">€{item.price.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* DRINKS SECTION */}
      <section id="drinks" className="relative overflow-hidden" data-testid="drinks-section">
        {/* Premium Drinks Header */}
        <div className="relative h-48 md:h-64 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${DRINKS_BG})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-hanse-dark/50 via-hanse-dark/60 to-hanse-dark/95" />
          
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="flex items-center gap-2 mb-4 bg-gradient-to-r from-cyan-500/20 to-cyan-600/10 backdrop-blur-sm px-4 py-2 rounded-full border border-cyan-400/30"
            >
              <Sparkles className="w-5 h-5 text-cyan-400" />
              <span className="text-cyan-300 font-semibold text-sm uppercase tracking-wider">
                {language === 'de' ? 'Erfrischend' : 'Refreshing'}
              </span>
              <Sparkles className="w-5 h-5 text-cyan-400" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3"
            >
              {t('menu.drinks.title')}
            </motion.h2>
          </div>
        </div>

        {/* Drinks Grid - Premium */}
        <div className="bg-gradient-to-b from-hanse-dark to-hanse-navy py-10 md:py-14">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {drinksData.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(index * 0.02, 0.15) }}
                  className="group bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-cyan-400/30 hover:bg-white/10 transition-all duration-300 text-center"
                  data-testid={`drink-item-${item.id}`}
                >
                  <h4 className="font-bold text-white text-sm mb-1 group-hover:text-cyan-300 transition-colors">
                    {language === 'de' ? item.name : (item.nameEn || item.name)}
                  </h4>
                  <p className="text-white/40 text-xs mb-2">{item.size}</p>
                  <span className="text-amber-400 font-bold">€{item.price.toFixed(2)}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FingerfoodDrinksSection;
